/**
 * User Management Page
 *
 * Admin interface to view, search, filter, and manage all system users.
 * Supports managing companies, consultants, auditors, and admin users.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserCog, Filter } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { Table } from '../../components/common/Table';
import { EmptyState } from '../../components/common/EmptyState';
import { useToastHelpers } from '../../components/feedback';
import { formatDate } from '../../utils/formatting';
import { PermissionWrapper } from '../../components/auth';
import { UserPermission } from '../../types/user';
import { getAllUsers } from '../../services/mockData/users';

// Load users from mock data
const mockUsers = getAllUsers();

export const UserManagementPage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const { success } = useToastHelpers();
  const [users] = useState(mockUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Apply filters
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.organization.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'ALL' || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Statistics
  const stats = {
    total: users.length,
    company: users.filter(u => u.role === 'COMPANY').length,
    consultant: users.filter(u => u.role === 'CONSULTANT').length,
    auditor: users.filter(u => u.role === 'AUDITOR').length,
    admin: users.filter(u => u.role === 'ADMIN').length,
  };

  const getRoleBadgeVariant = (role: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (role) {
      case 'ADMIN': return 'error';
      case 'AUDITOR': return 'warning';
      case 'CONSULTANT': return 'info';
      case 'COMPANY': return 'success';
      default: return 'default';
    }
  };

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'INACTIVE': return 'default';
      case 'SUSPENDED': return 'error';
      default: return 'default';
    }
  };

  const handleViewUser = (id: string) => {
    window.location.href = `/admin/users/${id}`;
  };

  const handleSuspendUser = (id: string) => {
    const user = users.find(u => u.id === id);
    if (window.confirm(t('admin:users.confirmSuspend'))) {
      // Mock API call
      success(`User ${user?.name} has been suspended successfully`, 'User Suspended');
    }
  };

  const handleActivateUser = (id: string) => {
    const user = users.find(u => u.id === id);
    if (window.confirm(t('admin:users.confirmActivate'))) {
      // Mock API call
      success(`User ${user?.name} has been activated successfully`, 'User Activated');
    }
  };

  // Commented out for future implementation
  // const handleDeleteUser = (id: string) => {
  //   const user = users.find(u => u.id === id);
  //   if (window.confirm(t('admin:users.confirmDelete'))) {
  //     alert(`User ${user?.name} deleted (mock)`);
  //   }
  // };

  const columns = [
    {
      key: 'name',
      header: t('admin:users.name'),
      render: (user: typeof mockUsers[0]) => (
        <div>
          <div className="font-medium text-gray-800">{user.name}</div>
          <div className="text-sm text-gray-500">{user.email}</div>
        </div>
      ),
    },
    {
      key: 'role',
      header: t('admin:users.role'),
      render: (user: typeof mockUsers[0]) => (
        <Badge variant={getRoleBadgeVariant(user.role)}>
          {t(`admin:roles.${user.role.toLowerCase()}`)}
        </Badge>
      ),
    },
    {
      key: 'organization',
      header: t('admin:users.organization'),
      render: (user: typeof mockUsers[0]) => (
        <span className="text-sm text-gray-700">{user.organization}</span>
      ),
    },
    {
      key: 'status',
      header: t('admin:users.status'),
      render: (user: typeof mockUsers[0]) => (
        <Badge variant={getStatusBadgeVariant(user.status)}>
          {t(`admin:status.${user.status.toLowerCase()}`)}
        </Badge>
      ),
    },
    {
      key: 'registrationDate',
      header: t('admin:users.registrationDate'),
      render: (user: typeof mockUsers[0]) => (
        <span className="text-sm text-gray-700">{formatDate(user.registrationDate)}</span>
      ),
    },
    {
      key: 'lastLogin',
      header: t('admin:users.lastLogin'),
      render: (user: typeof mockUsers[0]) => (
        <span className="text-sm text-gray-700">{formatDate(user.lastLogin)}</span>
      ),
    },
    {
      key: 'actions',
      header: t('admin:users.actions'),
      render: (user: typeof mockUsers[0]) => (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleViewUser(user.id)}>
            {t('admin:actions.view')}
          </Button>
          {user.status === 'ACTIVE' && (
            <PermissionWrapper permission={UserPermission.MANAGE_USERS}>
              <Button variant="outline" size="sm" onClick={() => handleSuspendUser(user.id)}>
                {t('admin:users.suspend')}
              </Button>
            </PermissionWrapper>
          )}
          {user.status === 'SUSPENDED' && (
            <PermissionWrapper permission={UserPermission.MANAGE_USERS}>
              <Button variant="outline" size="sm" onClick={() => handleActivateUser(user.id)}>
                {t('admin:users.activate')}
              </Button>
            </PermissionWrapper>
          )}
        </div>
      ),
    },
  ];

  const roleOptions = [
    { value: 'ALL', label: t('admin:users.allRoles') },
    { value: 'COMPANY', label: t('admin:roles.company') },
    { value: 'CONSULTANT', label: t('admin:roles.consultant') },
    { value: 'AUDITOR', label: t('admin:roles.auditor') },
    { value: 'ADMIN', label: t('admin:roles.admin') },
  ];

  const statusOptions = [
    { value: 'ALL', label: t('admin:users.allStatuses') },
    { value: 'ACTIVE', label: t('admin:status.active') },
    { value: 'INACTIVE', label: t('admin:status.inactive') },
    { value: 'SUSPENDED', label: t('admin:status.suspended') },
  ];

  return (
    <PageLayout
      pageTitle={t('admin:users.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('admin:dashboard.title'), href: '/admin/dashboard' },
        { label: t('admin:users.title') },
      ]}
    >
      {/* User Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">{t('admin:users.totalUsers')}</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">{t('admin:users.companyUsers')}</p>
            <p className="text-2xl font-bold text-green-600">{stats.company}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">{t('admin:users.consultantUsers')}</p>
            <p className="text-2xl font-bold text-blue-600">{stats.consultant}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">{t('admin:users.auditorUsers')}</p>
            <p className="text-2xl font-bold text-orange-600">{stats.auditor}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">{t('admin:users.adminUsers')}</p>
            <p className="text-2xl font-bold text-red-600">{stats.admin}</p>
          </CardBody>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin:users.searchUsers')}
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or organization..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin:users.filterByRole')}
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {roleOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin:users.filterByStatus')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Users Table */}
      <Card>
        <CardBody>
          {filteredUsers.length === 0 ? (
            users.length === 0 ? (
              <EmptyState
                icon={<UserCog className="w-10 h-10" />}
                title="No Users Yet"
                description="Your user directory is empty. Users will appear here once they register on the platform."
              />
            ) : (
              <EmptyState
                icon={<Filter className="w-10 h-10" />}
                title="No Matching Users"
                description="No users match your current search and filter criteria. Try adjusting the filters or search term."
                action={{
                  label: 'Clear All Filters',
                  onClick: () => {
                    setSearchTerm('');
                    setRoleFilter('ALL');
                    setStatusFilter('ALL');
                    setCurrentPage(1);
                  },
                }}
              />
            )
          ) : (
            <>
              <Table
                data={paginatedUsers}
                columns={columns}
                keyExtractor={(user) => user.id}
                isLoading={false}
              />
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </PageLayout>
  );
};
